import { useSearchParams } from "react-router-dom";
import { QUERY_PARAMS } from "@salesboard/contracts";
import { useFilterParam } from "@/hooks/useFilterParam";
import { useYears } from "@/features/opportunities/hooks";

export type PipelineView = "board" | "list";

/**
 * What the pipeline is looking at, independent of how it is drawn.
 *
 * **Year is a scope, not a filter.** It never appears in the filter panel and never becomes
 * a dismissable chip, because the board cannot render "all years" — clearing such a chip
 * would have to mean something different on each view, which is precisely the inconsistency
 * this increment removes. It is always valued, defaulting to the server's current year.
 *
 * Both views honour it. Until this increment the list ignored `opportunities:year` while the
 * board obeyed it, so one shared URL showed two different data sets depending on the toggle
 * — the view was changing the data, not just its presentation.
 */
export function usePipelineScope() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setViewParam] = useFilterParam("opportunities:view", "board");
  const years = useYears();

  const yearParam = searchParams.get("opportunities:year");
  // Undefined only for the first paint, before the window arrives. Callers gate their
  // queries on it rather than guessing a year the server may not agree with.
  const year = yearParam ? Number(yearParam) : years.data?.currentYear;

  const setYear = (next: number): void => {
    // See useFilterParam — read the live URL, not the render-time snapshot.
    const params = new URLSearchParams(window.location.search);
    // The default is absent from the URL, so a link to "this year" stays a clean link and
    // two people on the default see the same address.
    if (next === years.data?.currentYear) params.delete("opportunities:year");
    else params.set("opportunities:year", String(next));
    // Page 3 of 2026 is meaningless against 2024, and usually past its end — which would
    // strand the reader on an empty page whose pagination control does not render, with
    // no way back but the URL bar. Changing scope drops the page for the same reason
    // changing a filter does.
    params.delete(QUERY_PARAMS.page);
    setSearchParams(params, { replace: true });
  };

  return {
    view: (view === "list" ? "list" : "board") as PipelineView,
    setView: (next: PipelineView) => setViewParam(next),
    year,
    setYear,
    selectableYears: years.data?.selectableYears,
    editableYears: years.data?.editableYears,
    /** Whether the window itself is still arriving — callers scope their reads on it. */
    yearsPending: years.isPending,
    yearsError: years.error,
    refetchYears: years.refetch,
  };
}
